/* eslint-disable spire/export-styles */
import ADIColors from "@NA/Utils/ADIColors";
import { AIProtonModel } from "@NA/Widgets/AIProton/AIProtonModel";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedCurrency from "@insite/client-framework/Common/Utilities/getLocalizedCurrency";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { getUnitListPrice, getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";
import { isProductInventoryOnlyRole } from "@NA/Widgets/MyAccount/ADIMyAccount";

type OwnProps = TypographyPresentationProps & {
    pricing: ProductPriceDto;
    product: ProductContextModel | CartLineModel | AIProtonModel;
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    currencySymbol: string;
    qtyOrdered?: number;
    currentPage?: string;
};

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    roles: state.context.session.userRoles,
});

interface ProductPriceSavingMessageStyle {
    messageWarper?: InjectableCss;
    saveText?: TypographyProps;
    priceText?: TypographyProps;
}

export const productPriceSavingMessageStyle: ProductPriceSavingMessageStyle = {
    messageWarper: {},
    saveText: {
        css: css`
            color: ${ADIColors.lightGreen1};
            font-size: 12px;
            font-weight: 600;
            line-height: normal;
        `,
    },
    priceText: {
        css: css`
            color: ${ADIColors.gray4};
            font-size: 12px;
            font-weight: bold;
            text-decoration: line-through;
        `,
    },
};

const styles = productPriceSavingMessageStyle;

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

const ProductPriceSavingsMessage: FC<Props> = ({
    pricing,
    product,
    showSavingsAmount,
    showSavingsPercent,
    currencySymbol,
    qtyOrdered = 1,
    language,
    currentPage,
    roles,
    ...otherProps
}) => {
    const productContextModel = product as ProductContextModel;
    const cartLineModel = product as CartLineModel;
    const _MS_PER_DAY: number = 1000 * 60 * 60 * 24;
    const productData = productContextModel.product ? productContextModel.product : cartLineModel;

    const salesTypes = ["AC", "EV", "SM", "ED"];
    const saletype = productData?.properties["saletype"];
    const checkIfNotSales = saletype !== undefined && !salesTypes.includes(saletype);

    const isClearance = checkIfNotSales && (productData.properties["isClearance"] === "True" || (productData.properties["classificatioN_CODE"] && productData.properties["classificatioN_CODE"].toLowerCase() === "x"));
    const isSale =
        checkIfNotSales &&
        (productData.properties["classificatioN_CODE"] && productData.properties["classificatioN_CODE"] !== "X") &&
        productData.properties["onsalenow"] === "1";

    const { price: unitNetPrice } = getUnitNetPrice(pricing, qtyOrdered);
    const { price: unitListPrice, priceDisplay: unitListPriceDisplay } = getUnitListPrice(pricing, qtyOrdered);
    const hasPriceSavings = unitNetPrice < unitListPrice;
    if (!language || !pricing || !hasPriceSavings) {
        return null;
    }

    let isCartSaleClearanceFlag = false;
    if ((window.location.pathname).toLowerCase() === '/cart') {
        isCartSaleClearanceFlag = true;
    }
    const saleEndDate = () => {
        let basicSaleEndDate = "";
        if (pricing && pricing?.additionalResults && pricing?.additionalResults?.saleEndDate) {
            basicSaleEndDate = pricing.additionalResults.saleEndDate;

            var utcTime = new Date().getUTCHours();
            var today = new Date().setHours(utcTime - 5);
            var saleUtcHours = new Date(basicSaleEndDate).getUTCHours();
            var seDate = new Date(basicSaleEndDate).setHours(saleUtcHours - 5);

            var td = new Date(today);
            var sd = new Date(seDate);
            const utc1 = Date.UTC(td.getFullYear(), td.getMonth(), td.getDate());
            const utc2 = Date.UTC(sd.getFullYear(), sd.getMonth(), sd.getDate());
            var diff: number = Math.floor((utc2 - utc1) / _MS_PER_DAY);
            if (sd <= td) {
                return "";
            }
            const splitedDate = basicSaleEndDate.split("/");
            if (splitedDate.length > 1) {
                basicSaleEndDate = `${splitedDate[0]}/${splitedDate[1]}`;
            }
        }
        return basicSaleEndDate;
    };

    const savingsAmount = Math.round((unitListPrice - unitNetPrice) * 100) / 100;
    const localizedSavingsAmount = getLocalizedCurrency({ amount: savingsAmount, language, currencySymbol });
    const getSaleTypeText = (saleType: string): string => {
        const saleTypeText: Record<string, string> = {
            BF: "Black Friday",
            FS: "Flash Sale",
            LT: "Limited Time",
            SD: "Special Deal",
            WS: "Online Only",
            OO: "Online Only",
            DD: "Daily Deal",
            CD: "Cyber Deal",
            ED: "Expo Deal",
            EV: "Everyday Value",
            SM: "Save More",
        };
        return (saleTypeText[saleType]) || "Sale";
    };

    return (
        <>
            {isClearance && !isProductInventoryOnlyRole(roles) && (
                <StyledWrapper {...styles.messageWarper}>
                    {isCartSaleClearanceFlag && (
                        <Typography as="p" {...styles.saveText}>
                            Clearance
                        </Typography>
                    )}
                    <Typography as="p" {...styles.priceText}>
                        {unitListPriceDisplay}
                    </Typography>
                    {showSavingsAmount && (
                        <Typography as="p" {...styles.saveText}>
                            Save {localizedSavingsAmount}
                        </Typography>
                    )}
                    {showSavingsAmount && (
                        <Typography as="p" {...styles.saveText}>
                            {saleEndDate() && <>Ends {saleEndDate()}</>}
                        </Typography>
                    )}
                </StyledWrapper>
            )}

            {isSale && !isProductInventoryOnlyRole(roles) && (
                <StyledWrapper {...styles.messageWarper}>
                    {isCartSaleClearanceFlag && (
                        <Typography as="p" {...styles.saveText}>
                            {translate(getSaleTypeText(saletype))}
                        </Typography>
                    )}
                    <Typography as="p" {...styles.priceText}>
                        {unitListPriceDisplay}
                    </Typography>
                    {showSavingsAmount && (
                        <Typography as="p" {...styles.saveText}>
                            {saleEndDate() && <>Ends {saleEndDate()}</>}
                        </Typography>
                    )}
                </StyledWrapper>
            )}
        </>
    );
};

export default connect(mapStateToProps)(ProductPriceSavingsMessage);
